<template>
  <v-row>
    <v-col cols="12">
      <v-row dense>
        <v-col cols="12" xs="12" md="3">
          <v-text-field
            v-model="form.name"
            :rules="rules.requiredTextLine"
            :label="`${$t('ITEM.NAME')} *`"
            :readonly="readonly"
            :error-messages="errors.name"
            @input="updateValue('name', $event)"
          />
        </v-col>
        <v-col cols="12" xs="12" md="3">
          <zip-code-input
            v-model="form.zip"
            :error-messages="errors.zip"
            :readonly="readonly"
            @input="updateValue('zip', $event)"
          />
        </v-col>
        <v-col cols="12" xs="12" md="3">
          <v-text-field
            v-model="form.city"
            :label="$t('PROFILE.CITY')"
            :error-messages="errors.city"
            :readonly="readonly"
            @input="updateValue('city', $event)"
          />
        </v-col>
        <v-col cols="12" xs="12" md="3">
          <v-select
            v-model="form.status"
            :items="statuses"
            :loading="!!!statuses.length"
            :rules="rules.requiredTextLine"
            item-text="name"
            item-value="id"
            :label="`${$t('PROFILE.STATUS')} *`"
            :error-messages="errors.status"
            :readonly="readonly"
            @change="updateValue('status', $event)"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" xs="12" md="3">
          <company-select
            v-model="form.business_customer_id"
            :loading="!!!businessCustomers.length"
            :items="businessCustomers"
            item-text="name"
            item-value="id"
            :rules="rules.requiredSelectNotArray"
            :label="`${$t('BUILDING_COMPLEXES.BUSINESS_CUSTOMER')} *`"
            :error-messages="errors.business_customer_id"
            :readonly="readonly"
            @change="updateValue('business_customer_id', $event)"
          />
        </v-col>
        <v-col cols="12" xs="12" md="3">
          <company-select
            v-model="form.management_company_id"
            :loading="!!!managementCompanies.length"
            :items="managementCompanies"
            item-text="name"
            item-value="id"
            :rules="rules.requiredSelectNotArray"
            :label="`${$t('BUILDING_COMPLEXES.MANAGEMENT_COMPANY')} *`"
            :error-messages="errors.management_company_id"
            :readonly="readonly"
            @change="updateValue('management_company_id', $event)"
          />
        </v-col>
        <v-col cols="12" xs="12" md="3">
          <v-autocomplete
            v-model="form.concierge_id"
            :loading="!!!concierges.length"
            :items="concierges"
            item-text="full_name"
            item-value="user_id"
            :rules="rules.requiredSelectNotArray"
            :label="`${$t('BUILDING_COMPLEXES.CONCIERGE')} *`"
            :error-messages="errors.concierge_id"
            :readonly="readonly"
            :clearable="!readonly"
            @change="updateValue('concierge_id', $event)"
          >
            <template v-if="readonly && form.concierge_id" v-slot:append>
              <v-icon @click="showConcierge()">mdi-eye</v-icon>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" xs="12" md="3">
          <v-select
            v-model="form.type"
            :loading="!!!types.length"
            :items="types"
            item-text="name"
            item-value="id"
            :rules="rules.requiredSelectNotArray"
            :label="`${$t('ITEM.TYPE')}`"
            :error-messages="errors.type"
            :readonly="readonly"
            @change="updateValue('type', $event)"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col>
          <v-card min-height="142" elevation="0" class="mt-2">
            <v-card-text class="pa-0">
              <building-complex-tabs-form
                v-model="form"
                :readonly="readonly"
                :action="action"
                :errors="errors"
                @input="emitter"
                @update-prop="updateProp(...arguments)"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { mask } from 'vue-the-mask';
import formEditable from '@/modules/_global/mixins/form-editable';
import { mapCacheActions } from 'vuex-cache';
import { mapActions } from 'vuex';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import {
  BUSINESS_CUSTOMER,
  VERWALTUNG
} from '@/modules/companies/common/companyTypes';
import { CONCIERGE } from '@/modules/persons/common/roles';
import {
  requiredTextLine,
  requiredSelectArray,
  textDescription
} from '@/modules/_global/variables/rules.js';
import BuildingComplexTabsForm from '@/modules/building_complexes/components/forms/BuildingComplexTabsForm';
import CompanySelect from '@/modules/building_complexes/components/CompanySelect';
import ZipCodeInput from '@/modules/_global/_components/zip/ZipCodeInput';

export default {
  name: 'BuildingComplexForm',

  directives: {
    mask
  },

  components: { ZipCodeInput, CompanySelect, BuildingComplexTabsForm },

  mixins: [formEditable, i18nRefreshData],

  data() {
    return {
      form: {
        link_to_documentation: ''
      },

      types: [],
      statuses: [],
      businessCustomers: [],
      managementCompanies: [],
      concierges: [],

      rules: {
        requiredTextLine,
        requiredSelectArray,
        textDescription
      }
    };
  },

  mounted() {
    this.refreshData();
  },

  methods: {
    ...mapCacheActions('building_complex', ['getTypes', 'getStatuses']),
    ...mapActions('company', ['retrieveCompaniesByType']),
    ...mapActions('person', ['retrievePersonsByRole']),

    getDataConcierges() {
      this.retrievePersonsByRole(CONCIERGE).then(({ data }) => {
        this.concierges = data;
      });
    },

    getDataBusinessCustomers() {
      this.retrieveCompaniesByType(BUSINESS_CUSTOMER).then(({ data }) => {
        this.businessCustomers = data;
      });
    },

    getDataManagementCompanies() {
      this.retrieveCompaniesByType(VERWALTUNG).then(({ data }) => {
        this.managementCompanies = data;
      });
    },

    getDataTypes() {
      this.getTypes().then(({ data }) => {
        this.types = data.data;
      });
    },

    getDataStatuses() {
      this.getStatuses().then(({ data }) => {
        this.statuses = data.data;
      });
    },

    refreshData() {
      this.getDataTypes();
      this.getDataStatuses();
      this.getDataBusinessCustomers();
      this.getDataManagementCompanies();
      this.getDataConcierges();
    },

    showConcierge(id = this.form.concierge_id) {
      this.$router.push({
        name: 'persons.show',
        params: {
          id: id
        }
      });
    }
  }
};
</script>

<style scoped></style>
